import { useState } from "react";
import { Flag, X } from "lucide-react";
import { flagQuestion } from "@/lib/api";

const REASONS = [
  "Question is unclear",
  "One of the options looks wrong",
  "The correct answer seems incorrect",
  "Something else",
];

/**
 * Renders the "Something wrong with this question?" link + flag modal.
 * Props:
 *   recordId   — question record_id
 *   collection — "fuel_questions" | "fm_questions"
 *   sessionId  — active session UUID (used server-side to derive learner_id)
 */
export default function QuestionFlagTrigger({ recordId, collection, sessionId }) {
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [submitted, setSubmitted] = useState(false);

  const reset = () => {
    setOpen(false);
    setSelected(null);
    setSubmitted(false);
  };

  const handleSubmit = async () => {
    if (!selected) return;
    try {
      await flagQuestion({ session_id: sessionId, record_id: recordId, collection, flag_reason: selected });
    } catch {
      // fire-and-forget — swallow errors silently
    }
    setSubmitted(true);
    setTimeout(reset, 2200);
  };

  return (
    <>
      {/* Trigger link */}
      <div className="mt-5 text-center">
        <button
          type="button"
          onClick={() => setOpen(true)}
          className="text-xs text-slate-400 hover:text-slate-500 transition-colors underline underline-offset-2"
          data-testid="flag-question-trigger"
        >
          Something wrong with this question?
        </button>
      </div>

      {/* Modal overlay */}
      {open && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(0,0,0,0.45)" }}
          onClick={(e) => { if (e.target === e.currentTarget) reset(); }}
        >
          <div
            className="relative w-full max-w-sm rounded-3xl shadow-2xl overflow-hidden"
            style={{ background: "#fff" }}
            data-testid="flag-question-modal"
          >
            {/* Header */}
            <div className="flex items-center justify-between px-6 pt-6 pb-3">
              <div className="flex items-center gap-2">
                <Flag className="w-4 h-4 text-amber-500" />
                <h2 className="font-display text-lg font-bold text-slate-800">Flag this question</h2>
              </div>
              <button
                type="button"
                onClick={reset}
                className="text-slate-400 hover:text-slate-600 transition-colors p-1 rounded-lg"
                data-testid="flag-modal-close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            <div className="px-6 pb-6">
              {submitted ? (
                <div
                  className="flex items-center gap-2 py-6 justify-center text-emerald-600 font-semibold text-sm"
                  data-testid="flag-success-msg"
                >
                  Thanks — we'll look into it.
                </div>
              ) : (
                <>
                  <p className="text-sm text-slate-500 mb-4">What seems off?</p>

                  {/* Options */}
                  <div className="space-y-2" role="radiogroup">
                    {REASONS.map((reason) => {
                      const active = selected === reason;
                      return (
                        <button
                          key={reason}
                          type="button"
                          role="radio"
                          aria-checked={active}
                          onClick={() => setSelected(reason)}
                          className={`w-full text-left px-4 py-3 rounded-2xl border-2 text-sm font-medium transition-colors ${
                            active
                              ? "border-amber-400 bg-amber-50 text-slate-900"
                              : "border-slate-200 bg-white text-slate-600 hover:border-amber-300 hover:bg-amber-50/50"
                          }`}
                          data-testid={`flag-reason-${reason.replace(/\s+/g, "-").toLowerCase()}`}
                        >
                          {reason}
                        </button>
                      );
                    })}
                  </div>

                  {/* CTA */}
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={!selected}
                    className={`mt-5 w-full py-3 rounded-full font-semibold text-sm transition-colors ${
                      selected
                        ? "bg-amber-500 hover:bg-amber-600 text-white"
                        : "bg-slate-100 text-slate-400 cursor-not-allowed"
                    }`}
                    data-testid="flag-submit-btn"
                  >
                    Submit Flag
                  </button>

                  <div className="mt-3 text-center">
                    <button
                      type="button"
                      onClick={reset}
                      className="text-xs text-slate-400 hover:text-slate-600 transition-colors underline underline-offset-2"
                      data-testid="flag-cancel-btn"
                    >
                      Cancel
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
